import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Hash, Images, Inbox, LifeBuoy } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import {
  SUPPORT_ERRORS,
  TICKET_ORDER_CHANNEL,
  TICKET_ORDER_CHANNEL_LABELS,
  TICKET_RESOLUTION,
  formatTicketListSummary,
  formatTicketListTitle,
  listSupportTickets,
  updateSupportTicket,
} from '../../api/support';
import TicketDrawer from './TicketDrawer';
import { timeAgo } from '../../data/mockData';
import styles from './SupportRequests.module.css';

const TABS = [
  { key: 'OPEN', label: 'Open' },
  { key: 'IN_PROGRESS', label: 'In Progress' },
  { key: 'CLOSED', label: 'Closed' },
];

const CHANNEL_TABS = [
  { key: 'ALL', label: 'All channels' },
  { key: TICKET_ORDER_CHANNEL.DELIVERY, label: TICKET_ORDER_CHANNEL_LABELS[TICKET_ORDER_CHANNEL.DELIVERY] },
  { key: TICKET_ORDER_CHANNEL.SUBS, label: TICKET_ORDER_CHANNEL_LABELS[TICKET_ORDER_CHANNEL.SUBS] },
];

const EMPTY_COPY = {
  OPEN: 'New support tickets from customers and drivers will show up here.',
  IN_PROGRESS: 'Tickets you are actively working on will appear here.',
  CLOSED: 'Tickets you have resolved will appear here for reference.',
};

function friendlyError(err) {
  const code = err?.code || err?.message;
  return SUPPORT_ERRORS[code] || err?.detail || err?.message || 'Something went wrong.';
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function SupportRequests() {
  const { user } = useAuth();
  const { notify, refreshTicketBadges } = useOrders();

  const [tab, setTab] = useState('OPEN');
  const [channelTab, setChannelTab] = useState('ALL');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listSupportTickets(tab, user.idToken, { channel: channelTab });
      setTickets(list);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [tab, channelTab, user.idToken]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const switchTab = (key) => {
    setTab(key);
    setSelectedTicket(null);
  };

  const handleRespond = async (ticket, { status, opsNote, resolution, creditAmount }) => {
    const updated = await updateSupportTicket(
      ticket.ticketId,
      {
        customerSub: ticket.customerSub,
        status,
        opsNote,
        resolution,
        creditAmount,
      },
      user.idToken,
    );
    setSelectedTicket(null);
    if (resolution === TICKET_RESOLUTION.DISPOSE) {
      notify(`Ticket #${ticket.ticketId}: ₹${updated.creditAmount} store credit added`);
    } else if (resolution === TICKET_RESOLUTION.REQUIRE_RETURN) {
      const rmas = updated?.returnIds?.length
        ? ` · RMA ${updated.returnIds.join(', ')}`
        : '';
      notify(`Ticket #${ticket.ticketId}: return required${rmas}`);
    } else {
      notify(`Ticket #${ticket.ticketId} updated`);
    }
    await loadTickets();
    refreshTicketBadges();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Admin · Ops"
        title="Support Requests"
        subtitle="Review and respond to support tickets raised by customers and drivers."
      />

      <div className={styles.filterRow}>
        <div className={styles.filterTabs}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${styles.filterTab} ${tab === key ? styles.filterTabActive : ''}`}
              onClick={() => switchTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.channelFilterRow}>
        <span className={styles.channelFilterLabel}>Channel</span>
        <div className={styles.channelTabs}>
          {CHANNEL_TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${styles.channelTab} ${channelTab === key ? styles.channelTabActive : ''}`}
              onClick={() => {
                setChannelTab(key);
                setSelectedTicket(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className={styles.loadingText}>Loading tickets…</p>}

      {!loading && error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load support tickets"
          description={error}
          action={<Button variant="outline" onClick={loadTickets}>Retry</Button>}
        />
      )}

      {!loading && !error && tickets.length === 0 && (
        <EmptyState
          icon={Inbox}
          title={`No ${TABS.find((t) => t.key === tab).label.toLowerCase()} tickets`}
          description={
            channelTab === TICKET_ORDER_CHANNEL.SUBS
              ? 'No subscription-linked support tickets match this status. Try All channels, or check Return & Exchange for RMAs.'
              : EMPTY_COPY[tab]
          }
        />
      )}

      {!loading && !error && tickets.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Customer</th>
                <th>Order</th>
                <th>Raised</th>
                <th>Status</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.ticketId}>
                  <td>
                    <div className={styles.ticketCell}>
                      <div className={styles.ticketIcon}><LifeBuoy size={16} /></div>
                      <div>
                        <p className={styles.ticketSubject}>
                          {formatTicketListTitle(t)}
                          {t.orderChannel === TICKET_ORDER_CHANNEL.SUBS && (
                            <span className={styles.channelBadgeSubs}>
                              {TICKET_ORDER_CHANNEL_LABELS[TICKET_ORDER_CHANNEL.SUBS]}
                            </span>
                          )}
                        </p>
                        <p className={styles.ticketMessage} title={t.message}>
                          {formatTicketListSummary(t)}
                        </p>
                        {t.imageUrls?.length > 0 && (
                          <p className={styles.photoCount}>
                            <Images size={11} />
                            {t.imageUrls.length} photo{t.imageUrls.length === 1 ? '' : 's'}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={styles.subCell} title={t.customerSub}>{t.customerSub}</td>
                  <td className={styles.orderCell}>
                    {t.orderId ? (
                      <span className={styles.orderTag}><Hash size={11} />{t.orderId}</span>
                    ) : <span className={styles.muted}>—</span>}
                  </td>
                  <td className={styles.timeCell} title={formatDate(t.createdAt)}>
                    {timeAgo(t.createdAt)}
                  </td>
                  <td><StatusBadge status={t.status} size="sm" /></td>
                  <td className={styles.actionsCell}>
                    <Button variant="outline" size="sm" onClick={() => setSelectedTicket(t)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TicketDrawer
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
        onRespond={handleRespond}
      />
    </div>
  );
}
